import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import { Badge, BadgeContainer, Loader } from "@progress/kendo-react-indicators";
import { TextArea } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import { chevronRightIcon } from "@progress/kendo-svg-icons";
import { CreatingField } from "components/CreatingField";
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import { Link } from "react-router-dom";
import { JsonResponse } from "TypeGen/json-response";
import CarrierAutoComplete from "views/AssetCarriers/CarrierAutoComplete";
import DriverAutoComplete from "views/AssetDrivers/DriverAutoComplete";
import VehicleAutoComplete from "views/AssetVehicles/VehicleAutoComplete";
import Documents, { DocumentEntityType } from "views/Documents/Documents";
import { fetchApi } from "../../services/api";
import { ApiLink } from "../../TypeGen/api-link";
import { Title } from "../../utils/title";
import { ClaimStatuses } from "./FreightClaims";
import { openWindowPerc } from "../../services/openWindow";
import CustomerInput from "components/CustomerInput";
import CustomerContactInput from "components/CustomerContactInput";
import CarrierContactInput from "components/CarrierContactInput";
import { IDNameViewModel } from "TypeGen/id-name-view-model";

interface FreightClaimViewModel {
  ClaimID: number;
  ClaimStatus: number;
  ClaimType: string;
  ClaimAmount: number;
  CarrierID?: number | null;
  VehicleID?: number | null;
  DriverID?: number | null;
  CustomerID?: number | null;
  CustomerNumber?: string;
  CustomerContactID?: number | null;
  CarrierContactID?: number | null;
  OrderID: number;
  OrderNumber: number;
  CustomerClaimNumber?: string;
  InsuranceClaimNumber?: string;
  ThirdPartyName?: string;
  ThirdPartyContact?: string;
  ThirdPartyPhone?: string;
  FormalDemand?: number | null;
  ApprovedAmount?: number | null;
  Deductible?: number | null;
  OpenDate?: Date | null;
  ClosedDate?: Date | null;
  LossDate?: Date | null;
  JurisdictionState?: string;
  FollowUpDate?: Date | null;
  FollowUpReason?: string;
  LastStatusUpdate?: Date | null;
  StatusUpdateModifiedBy?: string;
  Description?: string;
  AdjusterName?: string;
  AdjusterPhone?: string;
  AdjusterEmail?: string;
  InsurerGroup?: string;
  PolicyNumber?: string;
  CreatedBy?: string;
  CreatedDateTime?: Date | null;
  ModifiedBy: string;
  ModifiedDateTime: Date;
  Version: number;
  DocumentCount: number;
  Links: ApiLink[];
}

const FreightClaim = () => {
  const { freightClaimId } = useParams<{ freightClaimId: string }>();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<FreightClaimViewModel>();
  const [showDocumentDialog, setShowDocumentDialog] = useState(false);
  const [saved, setSaved] = useState(false);
  const [customerContacts, setCustomerContacts] = useState<IDNameViewModel[]>([]);
  const [carrierContacts, setCarrierContacts] = useState<IDNameViewModel[]>([]);

  const populateContacts = useCallback((customerId: number) => {
    if (!customerId) return;
    fetchApi('/api/Quote/CustomerContacts', { CustomerID: customerId }, 'POST')
      .then((response: { Contacts: IDNameViewModel[] }) => {
        setCustomerContacts(response.Contacts);
      });
  }, []);

  const populateCarrierContacts = useCallback((carrierId: number) => {
    if (!carrierId) return;
    fetchApi('/api/Asset/CarrierContactsInput', { CarrierID: carrierId }, 'POST')
      .then((response: { Contacts: IDNameViewModel[] }) => {
        setCarrierContacts(response.Contacts);
      });
  }, []);

  const refresh = useCallback(() => {
    setLoading(true);
    fetchApi(`/api/Asset/FreightClaim/${freightClaimId}`)
      .then((response: any) => {
        setData(response);
        if (response.CustomerID) {
          populateContacts(response.CustomerID);
        } else {
          setCustomerContacts([]);
        }
        if (response.CarrierID) {
          populateCarrierContacts(response.CarrierID);
        } else {
          setCarrierContacts([]);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        alert("Unable to load cargo claim");
      });
  }, [freightClaimId, populateContacts, populateCarrierContacts]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (data?.CustomerID) {
      populateContacts(data.CustomerID);
    }
  }, [data?.CustomerID, populateContacts]);

  useEffect(() => {
    if (data?.CarrierID) {
      populateCarrierContacts(data.CarrierID);
    }
  }, [data?.CarrierID, populateCarrierContacts]);

  const save = () => {
    setLoading(true);
    const link = data.Links.find(x => x.Name === "Update") as ApiLink;
    fetchApi(link.Link, { Data: data }, link.Method)
      .then((res: JsonResponse) => {
        if (res.Success) {
          setSaved(true);
          setTimeout(() => setSaved(false), 1500);
          refresh();
        } else {
          alert(res.ErrorMessage);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        alert("Unable to save");
      });
  };

  if (loading || !data) {
    return <div className="text-center"><Loader type="converging-spinner" /></div>;
  }

  return (
    <div>
      <Title string="Cargo Claim" />
      <nav className="navbar navbar-expand-lg navbar-dark bg-dark mt-3">
        <span className="navbar-brand">
          <Link to="/Assets/FreightClaims">Cargo Claims</Link>
          <span className="text-muted">
            <SvgIcon icon={chevronRightIcon} />
            # {freightClaimId}
          </span>
        </span>
        <button className="navbar-toggler" type="button" data-toggle="collapse" data-target="#FreightClaimNav" aria-controls="FreightClaimNav" aria-expanded="false" aria-label="Toggle navigation">
          <span className="navbar-toggler-icon"></span>
        </button>
        <div className="collapse navbar-collapse" id="FreightClaimNav">
          <ul className="navbar-nav"></ul>
          <ul className="nav navbar-nav ml-auto">
            <BadgeContainer className="mr-2">
              <Button onClick={() => setShowDocumentDialog(true)}>Documents</Button>
              {data.DocumentCount > 0 && <Badge>{data.DocumentCount}</Badge>}
            </BadgeContainer>
          </ul>
        </div>
      </nav>
      <br />

      <h4 className="mt-3">Cargo Claim #{freightClaimId}</h4>

      {showDocumentDialog &&
        <Documents
          IsWindow
          EntityType={DocumentEntityType.FreightClaim}
          EntityId={parseInt(freightClaimId)}
          EntityNumber={`Claim # ${freightClaimId}`}
          CloseDocumentModal={() => setShowDocumentDialog(false)}
        />
      }

      {/* OrderID and OrderNumber display only */}
      <div className="row">
        <div className="form-group col-md-3">
          <Label>Claim #</Label>
          <div><strong>{data.ClaimID}</strong></div>
        </div>
        <div className="form-group col-md-3">
          <Label>Order Number</Label>
          <div>
            <a
              href="#"
              style={{ color: "#007bff" }}
              onClick={(e) => {
                e.preventDefault();
                openWindowPerc(`/Order/${data.OrderID}`);
              }}
            >
              {data.OrderNumber}
            </a>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="form-group col-md-3">
          <Label>Customer</Label>
          <div className="input-group">
            <CustomerInput
              CustomerID={data.CustomerID ?? 0}
              CustomerNumber={data.CustomerNumber ?? ''}
              onChange={(e) => {
                setData({ ...data, CustomerID: e.CustomerID || null, CustomerNumber: e.CustomerNumber, CustomerContactID: null });
                setCustomerContacts([]);
                if (e.CustomerID) populateContacts(e.CustomerID);
              }}
            />
          </div>
        </div>
        <div className="form-group col-md-3">
          <Label>Customer Contact</Label>
          <div className="input-group">
            <CustomerContactInput
              CustomerID={data.CustomerID ?? 0}
              CustomerContactID={data.CustomerContactID ?? 0}
              CustomerContacts={customerContacts}
              onChange={(e) => {
                if (e.CustomerID) {
                  setData({ ...data, CustomerID: e.CustomerID || null, CustomerNumber: e.CustomerNumber, CustomerContactID: e.CustomerContactID });
                  populateContacts(e.CustomerID);
                } else {
                  setData({ ...data, CustomerContactID: e.CustomerContactID });
                }
              }}
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="form-group col-md-3">
          <Label>Claim Status</Label>
          <CreatingField
            data={data.ClaimStatus}
            renderEditable={(value, onChange) => (
              <select className="custom-select" value={value} onChange={e => onChange(parseInt(e.target.value))}>
                {ClaimStatuses.filter(s => s.ID !== null).map(x => <option key={x.ID} value={x.ID}>{x.Name}</option>)}
              </select>
            )}
            onChange={val => setData({ ...data, ClaimStatus: val })}
          />
        </div>
        <div className="form-group col-md-3">
          <Label>Claim Type</Label>
          <CreatingField
            data={data.ClaimType}
            dataType="string"
            inputProps={{ maxLength: 100 }}
            onChange={val => setData({ ...data, ClaimType: val })}
          />
        </div>
        <div className="form-group col-md-3">
          <Label>Claim Amount</Label>
          <CreatingField
            data={data.ClaimAmount}
            dataType="number"
            inputProps={{ format: "c2" }}
            onChange={val => setData({ ...data, ClaimAmount: val })}
          />
        </div>
        <div className="form-group col-md-3">
          <Label>Formal Demand</Label>
          <CreatingField
            data={data.FormalDemand}
            dataType="number"
            inputProps={{ format: "c2" }}
            onChange={val => setData({ ...data, FormalDemand: val })}
          />
        </div>
      </div>

      <div className="row">
        <div className="form-group col-md-3">
          <Label>Approved Amount</Label>
          <CreatingField
            data={data.ApprovedAmount}
            dataType="number"
            inputProps={{ format: "c2" }}
            onChange={val => setData({ ...data, ApprovedAmount: val })}
          />
        </div>
        <div className="form-group col-md-3">
          <Label>Deductible</Label>
          <CreatingField
            data={data.Deductible}
            dataType="number"
            inputProps={{ format: "c2" }}
            onChange={val => setData({ ...data, Deductible: val })}
          />
        </div>
        <div className="form-group col-md-3">
          <Label>Loss Date</Label>
          <CreatingField
            data={data.LossDate != null ? dayjs.utc(data.LossDate).tz("America/New_York").toDate() : null}
            dataType="date"
            onChange={val => setData({ ...data, LossDate: val })}
          />
        </div>
        <div className="form-group col-md-3">
          <Label>Customer Claim #</Label>
          <CreatingField
            data={data.CustomerClaimNumber}
            inputProps={{ maxLength: 50 }}
            dataType="string"
            onChange={val => setData({ ...data, CustomerClaimNumber: val })}
          />
        </div>
      </div>

      <div className="row">
        <div className="form-group col-md-3">
          <Label>Insurance Claim #</Label>
          <CreatingField
            data={data.InsuranceClaimNumber}
            inputProps={{ maxLength: 50 }}
            dataType="string"
            onChange={val => setData({ ...data, InsuranceClaimNumber: val })}
          />
        </div>
        <div className="form-group col-md-3">
          <Label>Jurisdiction State</Label>
          <CreatingField
            data={data.JurisdictionState}
            inputProps={{ maxLength: 2 }}
            dataType="string"
            onChange={val => setData({ ...data, JurisdictionState: val })}
          />
        </div>
        <div className="form-group col-md-3">
          <Label>Open Date</Label>
          <CreatingField
            data={data.OpenDate != null ? dayjs.utc(data.OpenDate).tz("America/New_York").toDate() : null}
            dataType="date"
            onChange={val => setData({ ...data, OpenDate: val })}
          />
        </div>
        <div className="form-group col-md-3">
          <Label>Closed Date</Label>
          <CreatingField
            data={data.ClosedDate != null ? dayjs.utc(data.ClosedDate).tz("America/New_York").toDate() : null}
            dataType="date"
            onChange={val => setData({ ...data, ClosedDate: val })}
          />
        </div>
      </div>

      <div className="row">
        <div className="form-group col-md-3">
          <Label>Carrier</Label>
          <CarrierAutoComplete
            selectedCarrierID={data.CarrierID}
            onSelectedCarrier={c => {
              setData({ ...data, CarrierID: c?.CarrierID ?? null, CarrierContactID: null });
              setCarrierContacts([]);
              if (c?.CarrierID) populateCarrierContacts(c.CarrierID);
            }}
          />
        </div>
        <div className="form-group col-md-3">
          <Label>Carrier Contact</Label>
          <div className="input-group">
            <CarrierContactInput
              CarrierID={data.CarrierID ?? 0}
              CarrierContactID={data.CarrierContactID ?? 0}
              CarrierContacts={carrierContacts}
              onChange={(e) => {
                setData({ ...data, CarrierContactID: e.CarrierContactID });
              }}
            />
          </div>
        </div>
        <div className="form-group col-md-3">
          <Label>Vehicle</Label>
          <VehicleAutoComplete
            selectedVehicleID={data.VehicleID}
            onSelectedVehicle={v => setData({ ...data, VehicleID: v?.VehicleID ?? null })}
          />
        </div>
        <div className="form-group col-md-3">
          <Label>Driver</Label>
          <DriverAutoComplete
            selectedDriverID={data.DriverID}
            onSelectedDriver={d => setData({ ...data, DriverID: d?.DriverID ?? null })}
          />
        </div>
        {/*<div className="form-group col-md-3">*/}
        {/*  <Label>Last Status Update</Label>*/}
        {/*  <CreatingField            */}
        {/*    data={data.LastStatusUpdate != null ? dayjs.utc(data.LastStatusUpdate).tz("America/New_York").toDate() : null}*/}
        {/*    dataType="datetime"*/}
        {/*    onChange={val => setData({ ...data, LastStatusUpdate: val })}*/}
        {/*  />*/}
        {/*</div>*/}
      </div>

      <div className="row">
        <div className="form-group col-md-3">
          <Label>Third Party Name</Label>
          <CreatingField
            data={data.ThirdPartyName}
            inputProps={{ maxLength: 100 }}
            dataType="string"
            onChange={val => setData({ ...data, ThirdPartyName: val })}
          />
        </div>
        <div className="form-group col-md-3">
          <Label>Third Party Contact</Label>
          <CreatingField
            data={data.ThirdPartyContact}
            inputProps={{ maxLength: 100 }}
            dataType="string"
            onChange={val => setData({ ...data, ThirdPartyContact: val })}
          />
        </div>
        <div className="form-group col-md-3">
          <Label>Third Party Phone</Label>
          <CreatingField
            data={data.ThirdPartyPhone}
            inputProps={{ maxLength: 20 }}
            dataType="string"
            onChange={val => setData({ ...data, ThirdPartyPhone: val })}
          />
        </div>
      </div>
      <div className="row">
        <div className="form-group col-md-3">
          <Label>Adjuster Name</Label>
          <CreatingField
            data={data.AdjusterName}
            inputProps={{ maxLength: 100 }}
            dataType="string"
            onChange={val => setData({ ...data, AdjusterName: val })}
          />
        </div>
        <div className="form-group col-md-3">
          <Label>Adjuster Phone</Label>
          <CreatingField
            data={data.AdjusterPhone}
            inputProps={{ maxLength: 20 }}
            dataType="string"
            onChange={val => setData({ ...data, AdjusterPhone: val })}
          />
        </div>
        <div className="form-group col-md-3">
          <Label>Adjuster Email</Label>
          <CreatingField
            data={data.AdjusterEmail}
            inputProps={{ maxLength: 50 }}
            dataType="string"
            onChange={val => setData({ ...data, AdjusterEmail: val })}
          />
        </div>
        <div className="form-group col-md-3">
          <Label>Insurer Group</Label>
          <CreatingField
            data={data.InsurerGroup}
            inputProps={{ maxLength: 100 }}
            dataType="string"
            onChange={val => setData({ ...data, InsurerGroup: val })}
          />
        </div>
      </div>

      <div className="row">
        <div className="form-group col-md-3">
          <Label>Policy Number</Label>
          <CreatingField
            data={data.PolicyNumber}
            inputProps={{ maxLength: 50 }}
            dataType="string"
            onChange={val => setData({ ...data, PolicyNumber: val })}
          />
        </div>
        <div className="form-group col-md-6">
          <Label>Description of Loss</Label>
          <TextArea
            rows={5}
            value={data.Description}
            onChange={e => setData({ ...data, Description: e.value })}
          />
        </div>
      </div>

      <div className="row">
        <div className="form-group col-md-3">
          <Label>Follow Up Date</Label>
          <CreatingField
            data={data.FollowUpDate != null ? dayjs.utc(data.FollowUpDate).tz("America/New_York").toDate() : null}
            dataType="date"
            onChange={val => setData({ ...data, FollowUpDate: val })}
          />
        </div>
        <div className="form-group col-md-6">
          <Label>Follow Up Reason</Label>
          <TextArea
            rows={5}
            maxLength={2000}
            value={data.FollowUpReason ?? ''}
            onChange={e => setData({ ...data, FollowUpReason: e.value })}
          />
        </div>
      </div>

      <div className="row">
        <div className="form-group col-md-3">
          <Label>Modified Date</Label>
          <div>{dayjs.utc(data.ModifiedDateTime).tz("America/New_York").format("MM/DD/YYYY HH:mm")}</div>
        </div>
        <div className="form-group col-md-3">
          <Label>Created Date</Label>
          <div>{dayjs.utc(data.CreatedDateTime).tz("America/New_York").format("MM/DD/YYYY HH:mm")}</div>
        </div>
      </div>

      <div className="text-center mt-4">
        <Button onClick={() => setShowDocumentDialog(true)}>Documents</Button>
        <Button className="ml-2" themeColor={saved ? "success" : "primary"} onClick={save}>Save</Button>
      </div>
    </div>
  );
};

export default FreightClaim;